import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import { defaultScreeningProgress } from '@/types/screening/screening-progress';
import type {
    IQuickStartFormValues,
    IQuickStartModel,
    IQuickStartResponse,
    IQuickStartSubmitResult,
} from '@/types/screening/quick-start';

const QUICK_START_ENDPOINT = '/api/register/quick-start';

const defaultQuickStartModel: IQuickStartModel = {
    ...defaultScreeningProgress,
    currentRegistrationStep: DashRegistrationStep.QuickStart,

    isExistingEntry: false,
    needsName: false,

    firstName: '',
    lastName: '',

    gender: null,
    // birthDate: null,
    birthMonth: null,
    birthDay: null,
    birthYear: null,
    heightFeet: null,
    heightInches: null,
    weightLbs: null,

    showOfferToConnectToDonor: false,
    offerToConnectToDonor: false,
    isDonorConnectNegC: false,
    optOutOfDonorConnect: null,
    connectToDonorAcceptedDate: null,
    connectToDonorIsDesired: null,

    birthMonthList: [],
    birthDayList: [],
    birthYearList: [],
};

export const useQuickStart = (registrationId: number, editKey: string) => {
    const url = `${QUICK_START_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel<IQuickStartModel, IQuickStartResponse['results']>(
        url,
        defaultQuickStartModel,
        (r) => r.quickStart,
    );

    const { submit, isSubmitting, submitError } = useApiSubmit<IQuickStartFormValues, DashRegistrationStep, IQuickStartSubmitResult>(
        url,
        (values) => {
            // For the "negative C" opt-out variant the answer comes from the checkbox
            // (checked = opt out = not desired); otherwise it comes from the radios.
            const connectToDonorIsDesired = model.isDonorConnectNegC
                ? !values.optOutOfDonorConnect
                : toBoolOrNull(values.connectToDonorIsDesired);

            return {
                centerId: model.centerId,
                needsName: model.needsName,
                isExistingEntry: model.isExistingEntry,

                firstName: values.firstName,
                lastName: values.lastName,

                gender: toIntOrNull(values.gender),
                // birthDate: values.birthDate !== '' ? values.birthDate : null,
                birthMonth: toIntOrNull(values.birthMonth),
                birthDay: toIntOrNull(values.birthDay),
                birthYear: toIntOrNull(values.birthYear),
                heightFeet: toIntOrNull(values.heightFeet),
                heightInches: toIntOrNull(values.heightInches),
                weightLbs: toIntOrNull(values.weightLbs),

                showOfferToConnectToDonor: model.showOfferToConnectToDonor,
                offerToConnectToDonor: model.offerToConnectToDonor,
                isDonorConnectNegC: model.isDonorConnectNegC,
                optOutOfDonorConnect: values.optOutOfDonorConnect,
                connectToDonorAcceptedDate: model.connectToDonorAcceptedDate,
                connectToDonorIsDesired,
            };
        },
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
